// Frame 11's leaderboard card (33:1955) — PRD 16 §4 A2, now with the trend the
// PRD calls the difference between a subscription and a one-time audit: *"a
// static rank list answers 'what is bad'; a trend answers 'is it getting
// better,' which is the only question that recurs monthly."*
//
// THE MEASURE IS USAGE: look-ups per visit that opened the term. It replaced
// `confusion_score`, which multiplied hover count by dwell and added a
// 5x-weighted abandonment term — so a term ranked high either by being studied
// carefully or by being bounced off, and a bimodal term ranked highest of all.
// Nothing on this panel is a composite. Dwell is still shown on the term detail
// as its own engagement number and ranks nothing here.
//
// Every row is the entry point to frame 13, and this is the click walkthrough
// beat 6 measures, so two things here are load-bearing:
//
//  1. The link carries `doc` as well as the term id. A `term_id` is doc-local
//     (`t04` is a different term in every document), so a detail fetched
//     without it aggregates a mixture and the numbers stop agreeing with the
//     row that was clicked.
//  2. It is a router `Link`, not `ListRow`'s `href` prop. `ListRow` renders a
//     plain `<a>`, which full-reloads the SPA and remounts the shell — exactly
//     what beat 7's back-button criterion forbids. Noted for `polish`.
//
// AND TWO THINGS THAT KEEP THE TREND HONEST, both of which look like polish and
// are not:
//
//  3. **A term no visit opened shows no rate.** A rate needs a denominator;
//     with no visits there is not one, and the arithmetic would render that
//     absence as a confident `0.0` — "nobody opens this". The backend sends
//     `null`; this panel renders NOT_MEASURED with the backend's own reason and
//     never a number.
//  4. **The ranking is per visit.** A raw look-up count is a sum, so a term on
//     a busy page outranks a genuinely worse term on a deep page for free. The
//     backend ranks; this panel does not re-sort.

import { Link } from "@tanstack/react-router";
import { Badge, Card, EmptyState, Icon, List, ListRow } from "../../ui";
import { hrefs } from "../../shell/hrefs";
import type { RangeDays } from "./RangeChips";
import type { TermTrend } from "./api";
import { Sparkline } from "./Sparkline";
import {
  formatCount,
  formatDelta,
  formatLookupRate,
  formatShare,
  shownOf,
} from "./format";
import {
  LEADERBOARD_EMPTY_BODY,
  LEADERBOARD_EMPTY_HEADLINE,
  LEADERBOARD_SUBTITLE,
  LEADERBOARD_TITLE,
  LOOKUPS_PER_VISIT_WHY,
  NOT_MEASURED,
  NOT_MEASURED_WHY,
  NO_LOOKUPS_MEASURED,
  NO_LOOKUPS_WHY,
  NO_TREND_LINE,
  PER_VISIT_LABEL,
  SECTION_SHARE_LABEL,
  TREND_DELTA_WHY,
  TREND_FLAT,
  TREND_IMPROVED,
  TREND_WORSENED,
} from "./metricLabels";
import "./analytics.css";

/** Direction words, never colour alone — a red arrow is invisible to a reader
 *  who cannot see red, and this is the number they act on. Look-ups per visit
 *  FALLING is the good direction (readers stopped needing a second look), so
 *  `improved` is the nominal tone. */
const DIRECTION = {
  improved: { tone: "nominal" as const, arrow: "↓", words: TREND_IMPROVED },
  worsened: { tone: "flagged" as const, arrow: "↑", words: TREND_WORSENED },
  flat: { tone: "neutral" as const, arrow: "→", words: TREND_FLAT },
};

export type MostLookedUpProps = {
  orgSlug: string;
  days: RangeDays;
  /** Already ranked by the backend, on look-ups per visit. Do not re-sort. */
  terms: TermTrend[];
  /** Terms with rollup rows before measurability was considered. The gap to
   *  `terms.length` is the unmeasured tail and is stated in the footer. */
  termsFound: number;
  termsMeasured: number;
};

export function MostLookedUp({
  orgSlug,
  days,
  terms,
  termsFound,
  termsMeasured,
}: MostLookedUpProps) {
  return (
    <Card
      header={LEADERBOARD_TITLE}
      footer={
        <span className="type-bodyS">
          {termsFound === 0
            ? LEADERBOARD_SUBTITLE
            : `${shownOf(terms.length, termsFound, "term")} · ${termsMeasured} with a measured rate · ${LEADERBOARD_SUBTITLE}`}
        </span>
      }
    >
      {termsFound === 0 ? (
        <EmptyState
          headline={LEADERBOARD_EMPTY_HEADLINE}
          body={LEADERBOARD_EMPTY_BODY}
        />
      ) : (
        <List label={LEADERBOARD_TITLE}>
          {terms.map((term) => {
            const rate = term.current.lookups_per_session;
            const measured = rate !== null;
            const points = term.series.map((p) => p.lookups_per_session);
            const measuredDays = points.filter((v) => v !== null).length;
            const direction =
              term.direction === "not_measured" ? null : DIRECTION[term.direction];

            return (
              <ListRow
                key={`${term.doc_id}:${term.term_id}`}
                title={
                  <Link
                    className="maisight-analytics-row-link"
                    to={hrefs(orgSlug).term(term.term_id)}
                    search={{ doc: term.doc_id, days }}
                  >
                    {term.term_id}
                  </Link>
                }
                meta={
                  <span className="maisight-analytics-trend-meta">
                    <span
                      className="maisight-analytics-metric"
                      data-measured={measured ? "yes" : "no"}
                      title={measured ? LOOKUPS_PER_VISIT_WHY : NO_LOOKUPS_WHY}
                    >
                      {measured ? formatLookupRate(rate) : NO_LOOKUPS_MEASURED}
                    </span>
                    <span>
                      {`${term.doc_id} · ${formatCount(term.current.hover_count)} look-ups · ${formatCount(term.current.sessions)} visits · `}
                      <span
                        className="maisight-analytics-metric"
                        title={
                          term.current.share_of_section_views === null
                            ? NOT_MEASURED_WHY
                            : undefined
                        }
                        data-measured={
                          term.current.share_of_section_views === null ? "no" : "yes"
                        }
                      >
                        {term.current.share_of_section_views === null
                          ? `section share ${NOT_MEASURED}`
                          : `${formatShare(term.current.share_of_section_views)} ${SECTION_SHARE_LABEL}`}
                      </span>
                    </span>
                    {direction === null ? (
                      <span
                        className="maisight-analytics-metric"
                        data-measured="no"
                        title={TREND_DELTA_WHY}
                      >
                        {term.not_measured_reason ?? NOT_MEASURED}
                      </span>
                    ) : (
                      <span title={TREND_DELTA_WHY}>
                        {`${direction.arrow} ${formatDelta(term.delta_pct)} ${PER_VISIT_LABEL} — ${direction.words}`}
                      </span>
                    )}
                  </span>
                }
                trailing={
                  <>
                    {/* The sparkline is drawn only where something was measured.
                     *  A line over an unmeasured window would be flat on the
                     *  axis, which is a confident claim of "nobody opened it". */}
                    {measuredDays === 0 ? (
                      <span
                        className="maisight-analytics-metric maisight-analytics-noline"
                        data-measured="no"
                      >
                        {NO_TREND_LINE}
                      </span>
                    ) : (
                      <Sparkline
                        values={points}
                        label={`Look-ups per visit for ${term.term_id} over the last ${days} days, ${measuredDays} of ${points.length} days measured`}
                      />
                    )}
                    {/* The qualifier travels with the number: an unlabelled
                     *  rate reads as a raw total, which is the exact
                     *  misreading the per-visit denominator exists to stop. */}
                    <Badge tone={direction?.tone ?? "neutral"}>
                      <span title={measured ? LOOKUPS_PER_VISIT_WHY : NO_LOOKUPS_WHY}>
                        {formatLookupRate(rate)}
                      </span>
                    </Badge>
                    <Icon name="chevron-right" />
                  </>
                }
              />
            );
          })}
        </List>
      )}
    </Card>
  );
}
